import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatDate, useAuth } from '@halo/web-lib';
import {
  ArrowLeft,
  Shield,
  ShieldCheck,
  Loader2,
  Trash2,
  Clock,
  Store as StoreIcon,
  LogOut,
} from 'lucide-react';
import { DeleteUserModal } from '../components/DeleteUserModal';

interface StoreEntry {
  storeId: number;
  storeName: string;
  role: string;
  joinedAt: string;
}

interface SessionEntry {
  id: string;
  deviceInfo: string | null;
  createdAt: string;
  expiresAt: string;
}

interface UserDetail {
  id: string;
  email: string;
  displayName: string | null;
  role: number;
  createdAt: string;
  stores: StoreEntry[];
  sessions: {
    lastSessionAt: string | null;
    activeSessionCount: number;
    activeSessions: SessionEntry[];
  };
}

function userInitials(email: string, name: string | null): string {
  if (name) {
    const parts = name.split(' ');
    return parts.length > 1
      ? `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400',
  'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400',
  'bg-teal-100 text-teal-700',
  'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
  'bg-rose-100 text-rose-700',
  'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400',
  'bg-emerald-100 text-emerald-700',
  'bg-orange-100 text-orange-700',
];

function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function UserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [showDelete, setShowDelete] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: async ({ signal }) => {
      const res = await api.get<UserDetail>(`/api/v1/admin/users/${id}`, { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load user');
      return res.data;
    },
    enabled: id.length > 0,
  });

  const roleMutation = useMutation({
    mutationFn: async (role: number) => {
      const res = await api.put(`/api/v1/admin/users/${id}/role`, { role });
      if (!res.success) throw new Error(res.error ?? 'Failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/api/v1/admin/users/${id}/revoke-sessions`, {});
      if (!res.success) throw new Error(res.error ?? 'Failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', id] });
    },
  });

  if (isLoading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400 p-6">Loading user…</div>;
  }

  if (error || !data) {
    return (
      <div className="p-6 text-sm">
        <Link to="/users" className="text-blue-600 hover:underline">← Back to users</Link>
        <p className="mt-4 text-gray-500">User not found.</p>
      </div>
    );
  }

  const isAdmin = data.role === 0;
  const isSelf = currentUser?.userId === data.id;

  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/users" className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back to users
      </Link>

      {/* Profile card */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${isAdmin ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300' : avatarColor(data.id)}`}>
              {isAdmin ? <ShieldCheck className="w-5 h-5" /> : userInitials(data.email, data.displayName)}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{data.email}</h2>
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                {data.displayName && <span>{data.displayName}</span>}
                <span>·</span>
                <span><Clock className="w-3 h-3 inline mr-0.5" />Joined {formatDate(data.createdAt)}</span>
                <span>·</span>
                <span className={isAdmin ? 'text-amber-600' : ''}>
                  {isAdmin ? <ShieldCheck className="w-3 h-3 inline mr-0.5" /> : <Shield className="w-3 h-3 inline mr-0.5" />}
                  {isAdmin ? 'Admin' : 'User'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isAdmin ? (
              <button
                onClick={() => roleMutation.mutate(1)}
                disabled={roleMutation.isPending || isSelf}
                className="text-xs px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-40"
              >
                {roleMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Demote'}
              </button>
            ) : (
              <button
                onClick={() => roleMutation.mutate(0)}
                disabled={roleMutation.isPending}
                className="text-xs px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40"
              >
                {roleMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Make Admin'}
              </button>
            )}
            <button
              onClick={() => setShowDelete(true)}
              disabled={isSelf || isAdmin}
              title={isSelf ? 'Cannot delete your own account' : isAdmin ? 'Demote first' : 'Delete user'}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-3 h-3" /> Delete user
            </button>
          </div>
        </div>
      </div>

      {/* Stores card */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-1.5">
          <StoreIcon className="w-3.5 h-3.5 text-gray-400" /> Store access ({data.stores.length})
        </h3>
        {data.stores.length === 0 ? (
          <p className="text-xs text-gray-400">No store access</p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {data.stores.map((s) => (
              <div key={s.storeId} className="flex items-center justify-between py-2 text-sm">
                <Link to={`/stores/${s.storeId}`} className="text-gray-900 dark:text-gray-100 hover:text-blue-600">
                  {s.storeName}
                </Link>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="uppercase tracking-wide">{s.role}</span>
                  <span title={formatDate(s.joinedAt)}>{timeAgo(s.joinedAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sessions card */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sessions</h3>
          {confirmRevoke ? (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-red-600 dark:text-red-400">Revoke all sessions?</span>
              <button
                onClick={() => { revokeMutation.mutate(); setConfirmRevoke(false); }}
                disabled={revokeMutation.isPending}
                className="text-[10px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {revokeMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
              </button>
              <button
                onClick={() => setConfirmRevoke(false)}
                className="text-[10px] px-2 py-1 rounded border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950"
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmRevoke(true)}
              disabled={data.sessions.activeSessionCount === 0 || revokeMutation.isPending}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {revokeMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
              Revoke all sessions
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Last sign-in: {timeAgo(data.sessions.lastSessionAt)} · {data.sessions.activeSessionCount} active session{data.sessions.activeSessionCount === 1 ? '' : 's'}
        </p>
        {data.sessions.activeSessions.length > 0 && (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {data.sessions.activeSessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-2 text-xs">
                <span className="text-gray-700 dark:text-gray-300">{s.deviceInfo ?? 'Unknown device'}</span>
                <div className="flex items-center gap-3 text-gray-500">
                  <span title={formatDate(s.createdAt)}>Started {timeAgo(s.createdAt)}</span>
                  <span title={formatDate(s.expiresAt)}>Expires {timeAgo(s.expiresAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDelete && (
        <DeleteUserModal
          userId={data.id}
          email={data.email}
          onClose={() => setShowDelete(false)}
          onSuccess={() => {
            setShowDelete(false);
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
            navigate('/users');
          }}
        />
      )}
    </div>
  );
}
